import React from "react";
import { StyledTextInput, StyledButton } from "@aureskonnect/react-ui";
import { Alert, Modal, ModalBody, ModalFooter } from "reactstrap";
import { useTranslation } from "react-i18next";
import { AvForm } from "availity-reactstrap-validation";
import { useSnapshot } from "valtio";
import swal from "@sweetalert/with-react";
import NProgress from "nprogress";

import { store as vStore } from "@vStore";
import store, { setGroupName } from "../store";

import { SettingsSubFamiliesContext } from "../context";

type PropsType = {
    isModalOpened: boolean;
    isGroupNameEmpty: boolean;
    isNameExistsInGroupsList: boolean;
    toggleOpeningModal: () => void;
    setNewGroupDesignation: React.Dispatch<React.SetStateAction<string>>;
    setIsGroupNameEmpty: React.Dispatch<React.SetStateAction<boolean>>;
    setIsNameExistsInGroupsList: React.Dispatch<React.SetStateAction<boolean>>;
};

export function ModalWrapper({
    isModalOpened,
    isGroupNameEmpty,
    isNameExistsInGroupsList,
    toggleOpeningModal,
    setNewGroupDesignation,
    setIsGroupNameEmpty,
    setIsNameExistsInGroupsList,
}: PropsType) {
    const { t } = useTranslation();

    const { mainColor } = useSnapshot(vStore);
    const { groupName } = useSnapshot(store);

    const { groups: subFamiliesGroups } = React.useContext(
        SettingsSubFamiliesContext
    );

    const [
        isGroupNameContainsSpecialStrings,
        setIsGroupNameContainsSpecialStrings,
    ] = React.useState<boolean>(false);
    const [
        newGroupDesignationInModal,
        setNewGroupDesignationInModal,
    ] = React.useState<string>("");

    function handleNewGroupDesignationInputOnChangeEvent(
        e: React.ChangeEvent<HTMLInputElement>
    ) {
        let specialStrings: string[] = [
            "\\",
            "+",
            "-",
            "&",
            "/",
            "*",
            "!",
            "?",
            "ç",
            "#",
            "~",
            "^",
            "@",
            '"',
            "]",
            "}",
            "#",
            "{",
            "[",
            "|",
            "-",
            "'",
        ];

        setIsGroupNameContainsSpecialStrings(
            specialStrings.filter((string: any) => {
                return e.target.value.includes(string);
            }).length > 0
        );

        setIsGroupNameEmpty(
            e.target.value.length === 0 || e.target.value.trim().length === 0
        );

        setIsNameExistsInGroupsList(
            subFamiliesGroups.filter(
                (group: any) => group === e.target.value.trim()
            ).length > 0
        );

        setNewGroupDesignationInModal(e.target.value);
    }

    async function handleValidateNewGroupDesignationButtonOnClickEvent() {
        if (newGroupDesignationInModal !== "") {
            toggleOpeningModal();
            setIsGroupNameEmpty(false);
            setNewGroupDesignation(newGroupDesignationInModal);

            try {
                NProgress.start();

                await fetch(
                    `${process.env.REACT_APP_API_V1_URL}/settings/sub-families/edit-group-name`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            authorization: `Bearer ${localStorage.getItem(
                                "jwt"
                            )}`,
                        },
                        body: JSON.stringify({
                            "old-name": groupName,
                            "new-name": newGroupDesignationInModal,
                        }),
                        method: "PUT",
                    }
                )
                    .then((response) => response.json())
                    .then(async (data) => {
                        NProgress.done();

                        if (data.error) {
                            throw Error("Error while saving selection!");
                        }

                        setIsGroupNameEmpty(false);

                        setNewGroupDesignation(newGroupDesignationInModal);

                        setGroupName(newGroupDesignationInModal);

                        return swal({
                            icon: "success",
                            content: <p>{t("group updated successfully")}!</p>,
                            buttons: false,
                            timer: 2000,
                        });
                    });
            } catch (e) {
                NProgress.done();

                return swal({
                    icon: "error",
                    content: <p>{t("There's an error")!}</p>,
                    buttons: false,
                    timer: 2000,
                });
            }
        } else {
            setIsGroupNameEmpty(true);
        }
    }

    return (
        <Modal isOpen={isModalOpened} centered>
            <ModalBody className="justify-content-center">
                <p className="text-uppercase font-weight-bold font-size-16 text-center mt-4">
                    {t("Family groups")}
                </p>
                {isGroupNameEmpty ? (
                    <Alert className="mx-4" color="danger">
                        {t("Please enter the name of the group")}
                    </Alert>
                ) : null}
                {isGroupNameContainsSpecialStrings ? (
                    <Alert className="mx-4 mt-2" color="danger">
                        {t(
                            "The name should not contain any special characters"
                        )}
                    </Alert>
                ) : null}
                {isNameExistsInGroupsList ? (
                    <Alert className="mx-4 mt-2" color="danger">
                        {t("This group name is used before")}
                    </Alert>
                ) : null}
                <AvForm>
                    <StyledTextInput
                        id="families-group-designation"
                        name="families-group-designation"
                        defaultValue={newGroupDesignationInModal}
                        placeholder={t("Enter the designation")}
                        type="text"
                        className="mt-5 ml-4"
                        style={{ width: "90%" }}
                        onChange={handleNewGroupDesignationInputOnChangeEvent}
                    />
                </AvForm>
            </ModalBody>
            <ModalFooter className="justify-content-center mt-3">
                <StyledButton
                    onClick={toggleOpeningModal}
                    rounded={true}
                    outline={true}
                    variant={mainColor === null ? "primary" : ""}
                    className="w-25 white-space-nowrap"
                    style={{
                        color: mainColor,
                        borderColor: mainColor,
                    }}
                >
                    {t("Return")}
                </StyledButton>
                <StyledButton
                    rounded={true}
                    variant={mainColor === null ? "primary" : ""}
                    className="w-25 white-space-nowrap"
                    style={{
                        background: mainColor,
                    }}
                    disabled={
                        isGroupNameContainsSpecialStrings ||
                        isGroupNameEmpty ||
                        isGroupNameContainsSpecialStrings ||
                        isNameExistsInGroupsList
                    }
                    onClick={
                        handleValidateNewGroupDesignationButtonOnClickEvent
                    }
                >
                    {t("Validate")}
                </StyledButton>
            </ModalFooter>
        </Modal>
    );
}
